// One card on the Transfer Orders board — a single receiving warehouse or the summed "All
// warehouses" card of an aggregate direction. Title, a prominent clickable Total Shipments headline
// with the cell's total weight beneath, the three LIFECYCLE counts (yet_to_pick / in_transit /
// transferred — these sum to total), and a separate ERROR badge. Error is an overlay, not a fourth
// status: a stuck leg is already counted in its lifecycle bucket, so the badge sits apart from the
// lifecycle row and never tints it. Every figure drills down; the section owns direction / docks.
import { Card, Tag } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import React from "react";
import styles from "../index.module.scss";
import { TransferOrderCounts, TransferStatusKey } from "../control_tower.api";
import {
  TRANSFER_STATUS_META,
  TRANSFER_STATUS_ORDER,
  fmtTransferQty,
  fmtTransferWeight,
} from "../transfer_orders_meta";

interface Props {
  label: string;
  counts: TransferOrderCounts;
  loading?: boolean;
  // A lifecycle status, the error overlay (hasError), or neither = the Total Shipments headline.
  onDrill: (opts: { status?: TransferStatusKey; hasError?: boolean }) => void;
}

const TransferOrderCard: React.FC<Props> = ({ label, counts, loading, onDrill }) => (
  <Card size="small" title={label} className={styles.laneCard} loading={loading}>
    {/* Total shipments — the headline, clickable to the whole card (no status / error filter). */}
    <div className={styles.transferTotal} onClick={() => onDrill({})} role="button" tabIndex={0}>
      <span className={styles.transferTotalLabel}>Total Shipments</span>
      <span className={styles.transferTotalValue}>{counts.total_shipments}</span>
    </div>
    <div className={styles.transferWeight}>
      Qty {fmtTransferQty(counts.total_quantity)} · {fmtTransferWeight(counts.total_cargo_weight_kg)}
    </div>

    {/* Lifecycle bar — the three physical-progress buckets that sum to total. */}
    <div className={styles.statRow}>
      {TRANSFER_STATUS_ORDER.map((key) => (
        <div
          key={key}
          className={styles.statBlock}
          onClick={() => onDrill({ status: key })}
          role="button"
          tabIndex={0}
        >
          <div className={styles.statLabel}>{TRANSFER_STATUS_META[key].label}</div>
          <div className={styles.statValue}>{counts[key]}</div>
        </div>
      ))}
    </div>

    {/* Error overlay — a warning count sitting apart from the lifecycle bar. Shown only when there
        are stuck legs; nothing (just reserved height, for card alignment) when there are none. */}
    <div className={styles.transferErrorRow}>
      {counts.error > 0 && (
        <Tag
          color="error"
          icon={<WarningOutlined />}
          onClick={() => onDrill({ hasError: true })}
          style={{ cursor: "pointer", margin: 0 }}
        >
          {counts.error} stuck
        </Tag>
      )}
    </div>
  </Card>
);

export default TransferOrderCard;
